import React, {useEffect, useState} from 'react';
import {usePageMessage} from '../../core/hooks/usePageMessage';
import {ConditionRelationType, RelevantCondition} from '../types';

export const useRelevantConditionsValidation = (
  relevantConditions: RelevantCondition[],
  options: {pageMessageEnabled: boolean; consoleLogEnabled: boolean},
) => {
  const when = options.consoleLogEnabled || options.pageMessageEnabled;
  const [duplicatedRelevantConditions, setDuplicatedRelevantConditions] = useState<RelevantCondition[]>([]);
  const [invalidRelevantConditions, setInvalidRelevantConditions] = useState<RelevantCondition[]>([]);

  // Checks for invalid relevant conditions
  // ======================================

  useEffect(() => {
    if (when) {
      const timeoutId = setTimeout(() => {
        const invalidRelevantConditions =
          relevantConditions?.filter(
            (relevantCondition) =>
              relevantCondition.conditionRelationType === ConditionRelationType.Parent &&
              (relevantCondition.relatedConditionIds == null
                ? relevantCondition.relatedConditionId == null
                : relevantCondition.relatedConditionIds.length === 0),
          ) ?? [];
        setInvalidRelevantConditions(invalidRelevantConditions);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [relevantConditions, when]);

  usePageMessage(
    {
      dismissable: true,
      severity: 'error',
      content: (
        <span>
          There {invalidRelevantConditions.length === 1 ? 'is' : 'are'} {invalidRelevantConditions.length} invalid{' '}
          relevant {invalidRelevantConditions.length === 1 ? 'condition' : 'conditions'}.
        </span>
      ),
    },
    [options.pageMessageEnabled, invalidRelevantConditions],
    options.pageMessageEnabled && invalidRelevantConditions.length > 0,
  );

  useEffect(() => {
    if (options.consoleLogEnabled && invalidRelevantConditions.length > 0) {
      console.log('Invalid Relevant Features');
      console.log(invalidRelevantConditions);
    }
  }, [invalidRelevantConditions, options.consoleLogEnabled]);

  // Checks for duplicated relevant conditions
  // =========================================

  useEffect(() => {
    if (when) {
      const timeoutId = setTimeout(() => {
        const duplicatedRelevantConditions =
          relevantConditions?.filter((rc1, i1, a) =>
            a.some(
              (rc2, i2) =>
                i1 < i2 &&
                rc1.conditionId === rc2.conditionId &&
                rc1.conditionRelationType === rc2.conditionRelationType &&
                rc1.relatedConditionIds?.[0] === rc2.relatedConditionIds?.[0],
            ),
          ) ?? [];
        setDuplicatedRelevantConditions(duplicatedRelevantConditions);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [relevantConditions, when]);

  usePageMessage(
    {
      dismissable: true,
      severity: 'error',
      content: (
        <span>
          There {duplicatedRelevantConditions.length === 1 ? 'is' : 'are'} {duplicatedRelevantConditions.length}{' '}
          duplicated relevant {duplicatedRelevantConditions.length === 1 ? 'feature' : 'features'}.
        </span>
      ),
    },
    [options.pageMessageEnabled, duplicatedRelevantConditions],
    options.pageMessageEnabled && duplicatedRelevantConditions.length > 0,
  );

  useEffect(() => {
    if (options.consoleLogEnabled && duplicatedRelevantConditions.length > 0) {
      console.log('Duplicated Relevant Features');
      console.log(duplicatedRelevantConditions);
    }
  }, [invalidRelevantConditions, options.consoleLogEnabled]);
};
